import type { CeramicPackage, SelectedProtection } from '../data/protection-catalog.ts'

export const SELECTED_PROTECTION_STORAGE_KEY = 'autoglow.selectedProtection'
export const PROTECTION_QUERY_PARAM = 'protection'

export interface ProtectionStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export function createMemoryProtectionStorage(
  initial: Record<string, string> = {},
): ProtectionStorage {
  const values = new Map(Object.entries(initial))

  return {
    getItem(key) {
      return values.get(key) ?? null
    },
    setItem(key, value) {
      values.set(key, value)
    },
    removeItem(key) {
      values.delete(key)
    },
  }
}

function defaultProtectionStorage(): ProtectionStorage | null {
  if (typeof window === 'undefined') return null
  return window.sessionStorage
}

export function toSelectedProtection(item: CeramicPackage): SelectedProtection {
  return {
    id: item.id,
    slug: item.slug,
    durationLabel: item.displayName,
    productName: item.productName,
    durabilityLabel: item.durabilityLabel,
    startingPriceCents: item.startingPriceCents,
    currency: item.currency,
    warrantyEligible: item.warrantyEligible,
  }
}

export function parseProtectionIdFromSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const value = params.get(PROTECTION_QUERY_PARAM)?.trim()
  return value || null
}

export function buildProtectionQuoteHref(selection: SelectedProtection): string {
  const params = new URLSearchParams({ [PROTECTION_QUERY_PARAM]: selection.id })
  return `/contact?${params.toString()}`
}

export function buildProtectionBookHref(selection: SelectedProtection): string {
  const params = new URLSearchParams({ [PROTECTION_QUERY_PARAM]: selection.id })
  return `/book?${params.toString()}`
}

export function protectionBookingHref(item: CeramicPackage) {
  return buildProtectionBookHref(toSelectedProtection(item))
}

export function commitsProtectionToBookingRoute(options: {
  variant?: 'full' | 'preview'
  showContinueActions?: boolean
}) {
  return options.showContinueActions !== false
}

export function persistSelectedProtection(
  selection: SelectedProtection,
  storage: ProtectionStorage | null = defaultProtectionStorage(),
) {
  if (!storage) return
  storage.setItem(SELECTED_PROTECTION_STORAGE_KEY, JSON.stringify(selection))
}

export function readSelectedProtection(
  storage: ProtectionStorage | null = defaultProtectionStorage(),
): SelectedProtection | null {
  if (!storage) return null

  try {
    const stored = storage.getItem(SELECTED_PROTECTION_STORAGE_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored) as Partial<SelectedProtection>
    if (!parsed.id || !parsed.productName || typeof parsed.startingPriceCents !== 'number') {
      return null
    }
    return {
      id: parsed.id,
      slug: parsed.slug ?? parsed.id,
      durationLabel: parsed.durationLabel ?? parsed.id,
      productName: parsed.productName,
      durabilityLabel: parsed.durabilityLabel ?? '',
      startingPriceCents: parsed.startingPriceCents,
      currency: parsed.currency ?? 'AUD',
      warrantyEligible: Boolean(parsed.warrantyEligible),
    }
  } catch {
    return null
  }
}

export function resolveSelectedProtection(options: {
  search?: string
  packages: CeramicPackage[]
  storage?: ProtectionStorage | null
}): SelectedProtection | null {
  const fromQuery = parseProtectionIdFromSearch(options.search ?? '')
  if (fromQuery) {
    const queried = options.packages.find((item) => item.id === fromQuery)
    if (queried) return toSelectedProtection(queried)
  }

  return readSelectedProtection(options.storage ?? defaultProtectionStorage())
}
